import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  Users,
  Shield,
  User,
  Mail,
  Calendar,
  Edit,
  Trash2,
  MoreVertical,
  KeyRound,
  Undo2,
  EyeOff,
  Eye,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const AdminUsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'staff'
  });
  const [submitting, setSubmitting] = useState(false);

  // Edit / Delete state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Reset password state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resettingPwd, setResettingPwd] = useState(false);

  // Soft-delete: show deleted users toggle (Super Admin only)
  const [showDeleted, setShowDeleted] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users', {
        params: { include_deleted: showDeleted ? 'true' : 'false' },
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  async function handleRestore(u) {
    setRestoringId(u.id);
    try {
      await api.post(`/users/${u.id}/restore`);
      toast.success(`${u.name || u.email} restored`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore user');
    } finally {
      setRestoringId(null);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.post('/auth/register', formData);
      toast.success('User created successfully');
      setDialogOpen(false);
      fetchUsers();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      password: '',
      role: 'staff'
    });
  };

  const openEditDialog = (u) => {
    setEditTarget(u);
    setEditName(u.name || '');
    setEditDialogOpen(true);
  };

  const handleSaveName = async () => {
    if (!editTarget) return;
    const name = editName.trim();
    if (!name) {
      toast.error('Name is required');
      return;
    }
    setSavingEdit(true);
    try {
      await api.patch(`/users/${editTarget.id}`, { name });
      toast.success('User name updated');
      setEditDialogOpen(false);
      setEditTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingEdit(false);
    }
  };

  const openDeleteDialog = (u) => {
    setDeleteTarget(u);
    setDeleteDialogOpen(true);
  };

  const openResetDialog = (u) => {
    setResetTarget(u);
    setResetPassword('');
    setResetDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    if ((resetPassword || '').length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setResettingPwd(true);
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: resetPassword });
      toast.success(`Password reset for ${resetTarget.name}. User must change on next login.`);
      setResetDialogOpen(false);
      setResetTarget(null);
      setResetPassword('');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPwd(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success('User deleted');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-slate-200 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1e3a5f]" style={{ fontFamily: 'Playfair Display, serif' }}>
            User Management
          </h1>
          <p className="text-slate-500 mt-1">Manage system users and their roles</p>
        </div>
        <div className="flex items-center gap-2">
          {currentUser?.role === 'super_admin' && (
            <Button
              variant="outline"
              onClick={() => setShowDeleted((v) => !v)}
              data-testid="toggle-show-deleted-btn"
            >
              {showDeleted ? (
                <><EyeOff size={16} className="mr-2" /> Hide Deleted</>
              ) : (
                <><Eye size={16} className="mr-2" /> Show Deleted</>
              )}
            </Button>
          )}
          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-[#1e3a5f] hover:bg-[#162c46]"
            data-testid="add-user-btn"
          >
            <Plus size={18} className="mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{users.length}</p>
              <p className="text-sm text-slate-500">Total Users</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Shield size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {users.filter(u => u.role === 'super_admin').length}
              </p>
              <p className="text-sm text-slate-500">Admins</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <User size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {users.filter(u => u.role === 'staff').length}
              </p>
              <p className="text-sm text-slate-500">Staff</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users List */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#1e3a5f]">All Users</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-slate-100">
            {users.map((user) => {
              const isDeleted = !!user.deleted_at;
              return (
              <div 
                key={user.id} 
                className={`flex items-center justify-between py-4 ${isDeleted ? 'opacity-70' : ''}`}
                data-testid={`user-${user.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
                    isDeleted ? 'bg-slate-400' : (user.role === 'super_admin' ? 'bg-purple-500' : 'bg-blue-500')
                  }`}>
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-medium ${isDeleted ? 'text-slate-500 line-through' : 'text-slate-900'}`}>{user.name}</p>
                      {user.id === currentUser?.id && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">You</span>
                      )}
                      {isDeleted && (
                        <span
                          className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium"
                          data-testid={`user-deleted-badge-${user.id}`}
                        >
                          Deleted
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 flex-wrap">
                      <span className="flex items-center gap-1 text-sm text-slate-500">
                        <Mail size={14} />
                        {user.email}
                      </span>
                      <span className="flex items-center gap-1 text-sm text-slate-500">
                        <Calendar size={14} />
                        Joined {new Date(user.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {isDeleted && (
                      <p
                        className="text-xs text-red-600 mt-1"
                        data-testid={`user-deleted-info-${user.id}`}
                      >
                        Deleted by <span className="font-medium">{user.deleted_by_name || 'Unknown'}</span>
                        {' '}on {new Date(user.deleted_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    isDeleted ? 'bg-slate-100 text-slate-600' :
                    (user.role === 'super_admin' 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'bg-blue-100 text-blue-700')
                  }`}>
                    {user.role === 'super_admin' ? 'Admin' : 'Staff'}
                  </span>
                  {!isDeleted && user.must_change_password && (
                    <span className="px-2 py-1 rounded bg-amber-100 text-amber-700 text-xs">
                      Password Change Required
                    </span>
                  )}
                  {isDeleted && currentUser?.role === 'super_admin' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestore(user)}
                      disabled={restoringId === user.id}
                      data-testid={`restore-user-${user.id}`}
                    >
                      <Undo2 size={14} className="mr-1.5" />
                      {restoringId === user.id ? 'Restoring…' : 'Restore'}
                    </Button>
                  )}
                  {!isDeleted && user.role !== 'super_admin' && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          data-testid={`user-menu-${user.id}`}
                        >
                          <MoreVertical size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => openEditDialog(user)}
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit size={14} className="mr-2" />
                          Edit User Name
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openResetDialog(user)}
                          data-testid={`reset-password-${user.id}`}
                        >
                          <KeyRound size={14} className="mr-2" />
                          Reset Password
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openDeleteDialog(user)}
                          className="text-red-600"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 size={14} className="mr-2" />
                          Delete User
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Add New User
            </DialogTitle>
            <DialogDescription>
              Create a new user account. They will be required to change their password on first login.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter full name"
                  required
                  data-testid="user-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email address"
                  required
                  data-testid="user-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter temporary password"
                  required
                  data-testid="user-password-input"
                />
                <p className="text-xs text-slate-400">
                  User will be required to change this on first login.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Role *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#1e3a5f] hover:bg-[#162c46]"
                disabled={submitting}
                data-testid="submit-user-btn"
              >
                {submitting ? 'Creating...' : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Name Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Edit User Name
            </DialogTitle>
            <DialogDescription>
              Update the display name for <span className="font-medium">{editTarget?.email}</span>.
              Changes reflect immediately in all cases and assignments.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>User Name *</Label>
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Enter user name"
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); handleSaveName(); }
              }}
              autoFocus
              data-testid="edit-user-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveName}
              disabled={savingEdit || !editName.trim()}
              className="bg-[#1e3a5f] hover:bg-[#162c46]"
              data-testid="save-user-name-btn"
            >
              {savingEdit ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle
              className="flex items-center gap-2 text-red-600"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <Trash2 size={18} />
              Delete User
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-medium text-slate-900">{deleteTarget?.name}</span>
              {deleteTarget?.email ? ` (${deleteTarget.email})` : ''}?
              <br /><br />
              The user will be <b>soft-deleted</b> — their account is deactivated and a
              record of <i>who deleted them and when</i> is kept. You can restore them
              later via &quot;Show Deleted&quot;. If the user is currently assigned to active
              cases, you&apos;ll need to reassign those cases first.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              data-testid="cancel-delete-user-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-user-btn"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle
              className="flex items-center gap-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <KeyRound size={18} className="text-[#1e3a5f]" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for <span className="font-medium">{resetTarget?.name}</span>{' '}
              ({resetTarget?.email}). The user will be required to change this password on their
              next login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>New Password (min 8 chars) *</Label>
            <Input
              type="text"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              placeholder="Enter a temporary password"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleResetPassword(); } }}
              autoFocus
              data-testid="reset-password-input"
            />
            <p className="text-xs text-slate-500">Share this password with the user securely. They will change it on first login.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resettingPwd || resetPassword.length < 8}
              className="bg-[#1e3a5f] hover:bg-[#162c46]"
              data-testid="confirm-reset-password-btn"
            >
              {resettingPwd ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;
